import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ShieldCheck, Globe, Search, AlertCircle, CheckCircle2, Upload, ListFilter } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import api from '../api/axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const DomainSettings = () => {
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [bulkDomainsText, setBulkDomainsText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [brandingForm, setBrandingForm] = useState({
    brand_name: '',
    portal_name: '',
    primary_color: '#4f46e5',
    accent_color: '#a855f7',
    logo_url: '',
    support_email: '',
  });
  const [brandingLoading, setBrandingLoading] = useState(false);

  const fetchDomains = useCallback(async () => {
    try {
      const res = await api.get('/domains');
      if (res.data.success) {
        const list = res.data.data || [];
        setDomains(list);
        if (list.length > 0) {
          const selected = list.find((item) => String(item.id) === String(selectedDomainId)) || list[0];
          setSelectedDomainId(String(selected.id));
          setBrandingForm({
            brand_name: selected.brand_name || '',
            portal_name: selected.portal_name || '',
            primary_color: selected.primary_color || '#4f46e5',
            accent_color: selected.accent_color || '#a855f7',
            logo_url: selected.logo_url || '',
            support_email: selected.support_email || '',
          });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load domains');
    }
  }, [selectedDomainId]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain) return;

    const cleanDomain = newDomain.replace('@', '').trim().toLowerCase();
    if (!cleanDomain) return;

    if (domains.some(d => d.domain === cleanDomain)) {
      toast.error('This domain is already verified.');
      return;
    }

    try {
      setLoading(true);
      const res = await api.post('/domains', { domain: cleanDomain });
      if (res.data.success) {
        toast.success('Domain verified successfully');
        setNewDomain('');
        fetchDomains();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = async (e) => {
    e.preventDefault();
    const raw = bulkDomainsText.replace(/^@/gm, '').trim();
    if (!raw) {
      toast.error('Paste at least one domain.');
      return;
    }
    try {
      setBulkLoading(true);
      const res = await api.post('/domains/bulk', { domains: bulkDomainsText });
      if (res.data.success) {
        const { added, skipped } = res.data.data || {};
        if (added > 0) toast.success(`Added ${added} domain(s)${skipped ? `. ${skipped} skipped.` : ''}`);
        if (skipped > 0 && added === 0) toast.info('All domains were already in the list.');
        setBulkDomainsText('');
        fetchDomains();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add domains');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDelete = async (id, domainName) => {
    if (!window.confirm(`Are you sure? Users with @${domainName} will no longer be able to login.`)) return;
    try {
      await api.delete(`/domains/${id}`);
      toast.success('Domain removed');
      fetchDomains();
    } catch (err) {
      toast.error('Failed to delete domain');
    }
  };

  const filteredDomains = domains.filter(d =>
    d.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedDomain = domains.find((item) => String(item.id) === String(selectedDomainId));

  const handleBrandingSave = async (e) => {
    e.preventDefault();
    if (!selectedDomainId) return toast.error('Select a domain first.');
    try {
      setBrandingLoading(true);
      await api.patch(`/domains/${selectedDomainId}/branding`, brandingForm);
      toast.success('Tenant branding updated');
      fetchDomains();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update branding');
    } finally {
      setBrandingLoading(false);
    }
  };

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="flex flex-col overflow-hidden min-h-full bg-gradient-to-br from-slate-50 via-white to-blue-50/50 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950">

        {/* Header */}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-gray-200/60 dark:border-gray-700/60 px-8 py-6 shadow-sm z-20">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 dark:from-indigo-500/20 dark:to-purple-500/20 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 shadow-sm">
              <ShieldCheck className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                Domain Access Control
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
                Configure trusted email domains for secure organization access
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8 z-10">
          <div className="max-w-[1600px] mx-auto">

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">

              {/* LEFT COLUMN: Input Forms (Actions) */}
              <div className="xl:col-span-4 space-y-6">

                {/* Security Note Card */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 p-6 text-white shadow-xl shadow-indigo-500/20 group">
                  <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all duration-700"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm shadow-inner">
                        <AlertCircle className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-lg tracking-tight">Security Policy</h3>
                    </div>
                    <p className="text-indigo-100 text-sm leading-relaxed mb-5 font-medium">
                      Access is restricted. Only users with emails from verified domains can register or log in. Unverified domains are blocked.
                    </p>
                    <div className="flex items-center gap-2 text-xs font-bold bg-white/10 px-3 py-1.5 rounded-full w-fit border border-white/20 shadow-sm backdrop-blur-md">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                      {domains.length} Verified Domains Active
                    </div>
                  </div>
                </div>

                {/* Single Add Domain */}
                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-3xl border border-gray-200/60 dark:border-gray-700 shadow-lg shadow-gray-200/50 dark:shadow-black/20 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <Plus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Add Single Domain</h2>
                  </div>

                  <form onSubmit={handleAddDomain} className="flex gap-3">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <span className="text-gray-400 font-bold">@</span>
                      </div>
                      <Input
                        placeholder="company.com"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        className="pl-9 h-11 bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:bg-white dark:focus:bg-slate-900 transition-all"
                      />
                    </div>
                    <Button type="submit" disabled={loading || !newDomain} className="h-11 px-5 bg-indigo-600 hover:bg-indigo-500 shadow-md shadow-indigo-500/20">
                      {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Add'}
                    </Button>
                  </form>
                </div>

                {/* Bulk Upload */}
                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-3xl border border-gray-200/60 dark:border-gray-700 shadow-lg shadow-gray-200/50 dark:shadow-black/20 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <Upload className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Bulk Import</h2>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Paste domains separated by new lines.</p>

                  <form onSubmit={handleBulkAdd} className="space-y-4">
                    <textarea
                      placeholder={'google.com\ncantiktech.com\npartner.co.id'}
                      value={bulkDomainsText}
                      onChange={(e) => setBulkDomainsText(e.target.value)}
                      rows={5}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none font-mono"
                    />
                    <Button
                      type="submit"
                      disabled={bulkLoading || !bulkDomainsText.trim()}
                      className="w-full h-11 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:bg-slate-800 dark:border-slate-700 dark:text-gray-200 dark:hover:bg-slate-700 transition-all font-semibold"
                    >
                      {bulkLoading ? 'Importing...' : 'Import Domains'}
                    </Button>
                  </form>
                </div>

                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-3xl border border-gray-200/60 dark:border-gray-700 shadow-lg shadow-gray-200/50 dark:shadow-black/20 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                      <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Tenant Branding</h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Brand each verified domain with its own logo, colors, and portal text.</p>
                    </div>
                  </div>

                  <form onSubmit={handleBrandingSave} className="space-y-4">
                    <select
                      value={selectedDomainId}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        setSelectedDomainId(nextId);
                        const nextDomain = domains.find((item) => String(item.id) === String(nextId));
                        if (!nextDomain) return;
                        setBrandingForm({
                          brand_name: nextDomain.brand_name || '',
                          portal_name: nextDomain.portal_name || '',
                          primary_color: nextDomain.primary_color || '#4f46e5',
                          accent_color: nextDomain.accent_color || '#a855f7',
                          logo_url: nextDomain.logo_url || '',
                          support_email: nextDomain.support_email || '',
                        });
                      }}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    >
                      {domains.map((item) => (
                        <option key={item.id} value={item.id}>
                          @{item.domain}
                        </option>
                      ))}
                    </select>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        placeholder="Brand name"
                        value={brandingForm.brand_name}
                        onChange={(e) => setBrandingForm((prev) => ({ ...prev, brand_name: e.target.value }))}
                      />
                      <Input
                        placeholder="Portal name"
                        value={brandingForm.portal_name}
                        onChange={(e) => setBrandingForm((prev) => ({ ...prev, portal_name: e.target.value }))}
                      />
                      <Input
                        type="color"
                        value={brandingForm.primary_color}
                        onChange={(e) => setBrandingForm((prev) => ({ ...prev, primary_color: e.target.value }))}
                        className="h-11"
                      />
                      <Input
                        type="color"
                        value={brandingForm.accent_color}
                        onChange={(e) => setBrandingForm((prev) => ({ ...prev, accent_color: e.target.value }))}
                        className="h-11"
                      />
                    </div>

                    <Input
                      placeholder="Logo URL"
                      value={brandingForm.logo_url}
                      onChange={(e) => setBrandingForm((prev) => ({ ...prev, logo_url: e.target.value }))}
                    />
                    <Input
                      placeholder="Support email"
                      value={brandingForm.support_email}
                      onChange={(e) => setBrandingForm((prev) => ({ ...prev, support_email: e.target.value }))}
                    />

                    {selectedDomain && (
                      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50/80 dark:bg-slate-900/50">
                        <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Preview</p>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
                            style={{ background: `linear-gradient(135deg, ${brandingForm.primary_color}, ${brandingForm.accent_color})` }}
                          >
                            {(brandingForm.brand_name || selectedDomain.brand_name || 'B').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{brandingForm.brand_name || selectedDomain.brand_name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{brandingForm.portal_name || selectedDomain.portal_name}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button type="submit" disabled={brandingLoading || !selectedDomainId} className="w-full h-11 bg-indigo-600 hover:bg-indigo-500">
                      {brandingLoading ? 'Saving Branding...' : 'Save Branding'}
                    </Button>
                  </form>
                </div>

              </div>

              {/* RIGHT COLUMN: Domain List (Data) */}
              <div className="xl:col-span-8 h-full">
                <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-3xl border border-gray-200/60 dark:border-gray-700 shadow-lg shadow-gray-200/50 dark:shadow-black/20 h-full flex flex-col min-h-[600px]">

                  {/* Toolbar */}
                  <div className="px-8 py-6 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <ListFilter className="w-5 h-5 text-gray-400" />
                      <h3 className="font-bold text-gray-700 dark:text-gray-200 text-lg">Verified List</h3>
                      <span className="bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 text-xs px-2.5 py-0.5 rounded-full font-bold ml-2">
                        {domains.length}
                      </span>
                    </div>

                    <div className="relative w-full sm:w-72">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search verified domains..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* List Content */}
                  <div className="flex-1 overflow-y-auto p-2">
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                      {filteredDomains.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                          <div className="w-16 h-16 bg-gray-50 dark:bg-slate-700/50 rounded-full flex items-center justify-center mb-4">
                            <Globe className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                          </div>
                          <p className="font-semibold text-gray-500 dark:text-gray-400">No domains found</p>
                          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Add verified domains to secure your portal</p>
                        </div>
                      ) : (
                        filteredDomains.map((item) => (
                          <div key={item.id} className="group flex items-center justify-between p-4 hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 rounded-2xl transition-all duration-200 mx-2 my-1">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-2xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-600 group-hover:scale-105 transition-transform duration-200">
                                <Globe className="w-6 h-6 text-indigo-500 dark:text-indigo-400" />
                              </div>
                              <div>
                                <p className="font-bold text-gray-800 dark:text-white text-lg tracking-tight">@{item.domain}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                  </span>
                                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Active</span>
                                  <span className="text-gray-300 dark:text-gray-600 mx-1">•</span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    Added {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Recently'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleDelete(item.id, item.domain)}
                              className="p-3 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0"
                              title="Remove Domain"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DomainSettings;
